import { useEffect } from 'react';

/**
 * Mapa de rutas con sus importaciones lazy para preloading
 * Permite precargar componentes antes de que el usuario navegue a ellos
 */
export const routePreloadMap: Record<string, () => Promise<any>> = {
  '/caso': () => import('@/modules/casos/pages/Casos.page'),
  '/caso/ver-tramites': () => import('@/modules/casos/pages/VerTramites.page'),
  '/caso/crear': () => import('@/modules/casos/pages/CasosForm.page'),
  '/audiencias': () => import('@/modules/audiencias/pages/Audiencias.page'),
  '/calendario': () => import('@/modules/calendar/pages/Calendar.page'),
  '/archivo': () => import('@/modules/Archivo').then(m => ({ default: m.FilePage })),
  '/equipo': () => import('@/modules/equipo').then(m => ({ default: m.EquipoPage })),
  '/': () => import('@/modules/escritorio/pages/Escritorio.page'),
};

/**
 * Hook para precargar rutas críticas al cargar la aplicación
 * Útil para mejorar la experiencia del usuario al navegar
 * NOTA: Este hook no depende de useLocation, se ejecuta una sola vez al montar
 */
export const useRoutePreload = () => {
  useEffect(() => {
    // Precargar rutas más comunes después de cargar la aplicación
    const preloadCommonRoutes = () => {
      // Esperar 2 segundos después de la carga inicial para no interferir con la carga principal
      setTimeout(() => {
        // Precargar rutas más frecuentemente visitadas
        const criticalRoutes = ['/caso', '/audiencias', '/calendario'];

        criticalRoutes.forEach(route => {
          if (routePreloadMap[route]) {
            routePreloadMap[route]().catch(() => {
              // Silenciar errores de precarga
            });
          }
        });
      }, 2000);
    };

    preloadCommonRoutes();
  }, []); // Solo se ejecuta una vez al montar
};

/**
 * Helper para precargar una ruta específica
 * Útil para precarga en hover de links o botones
 */
export const preloadRoute = (path: string) => {
  const loader = routePreloadMap[path];
  if (loader) {
    return loader().catch(() => {
      // Silenciar errores de precarga
    });
  }
};

/**
 * Hook para precargar rutas al hacer hover en links
 * Uso: const handleMouseEnter = usePreloadOnHover('/ruta')
 */
export const usePreloadOnHover = (path: string) => {
  return () => {
    preloadRoute(path);
  };
};
